import { ApplicationRef, DestroyRef, Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { AppUpdateSnackbarComponent } from '@shared-components/app-update-snackbar/app-update-snackbar.component';
import { filter, fromEvent, interval, merge, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const UPDATE_POLL_INTERVAL_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private started = false;
  private checkInProgress = false;
  private isOffline = false;
  private updateReady = false;
  private activeToastKind: 'update' | 'offline' | null = null;
  private activeToastRef: MatSnackBarRef<AppUpdateSnackbarComponent> | null =
    null;

  constructor(
    private readonly appRef: ApplicationRef,
    private readonly router: Router,
    private readonly swUpdate: SwUpdate,
    private readonly snackBar: MatSnackBar,
    private readonly destroyRef: DestroyRef,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;

    const windowRef = this.document.defaultView;
    if (windowRef) {
      this.isOffline = !windowRef.navigator.onLine;
      fromEvent(windowRef, 'offline')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.handleOffline());
      fromEvent(windowRef, 'online')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.handleOnline());
    }
    if (this.isOffline) this.showOfflineToast();

    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(
        filter((event) => event.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.handleUpdateReady());

    this.appRef.isStable
      .pipe(
        filter((isStable) => isStable),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.startUpdateChecks());
  }

  private startUpdateChecks(): void {
    const navigationChecks = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    );
    const foregroundChecks = fromEvent(this.document, 'visibilitychange').pipe(
      filter(() => this.document.visibilityState === 'visible'),
    );

    merge(navigationChecks, foregroundChecks, interval(UPDATE_POLL_INTERVAL_MS))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.checkForUpdate());

    this.checkForUpdate();
  }

  private checkForUpdate(): void {
    if (this.checkInProgress) return;
    this.checkInProgress = true;

    void this.swUpdate
      .checkForUpdate()
      .catch((error: unknown) => {
        console.error(
          'No se pudo verificar si hay una nueva versión del FE.',
          error,
        );
      })
      .finally(() => {
        this.checkInProgress = false;
      });
  }

  private showUpdateAvailable(): void {
    this.openToast('update', {
      kind: 'update',
      message: 'Hay una nueva versión disponible.',
      actionLabel: 'Recargar',
      action: 'reload',
    });
  }

  private handleUpdateReady(): void {
    this.updateReady = true;
    if (!this.isOffline) this.showUpdateAvailable();
  }

  private handleOffline(): void {
    this.isOffline = true;
    this.showOfflineToast();
  }

  private handleOnline(): void {
    this.isOffline = false;
    if (this.activeToastKind === 'offline') this.dismissActiveToast();
    if (this.updateReady) this.showUpdateAvailable();
  }

  private showOfflineToast(): void {
    if (this.activeToastKind === 'offline') return;
    this.dismissActiveToast();
    this.openToast('offline', {
      kind: 'offline',
      message: 'Se perdió la conexión a internet.',
      actionLabel: 'Ok',
      action: 'dismiss',
    });
  }

  private openToast(
    kind: 'update' | 'offline',
    data: {
      kind: 'update' | 'offline';
      message: string;
      actionLabel: string;
      action: 'reload' | 'dismiss';
    },
  ): void {
    const snackBarRef = this.snackBar.openFromComponent(
      AppUpdateSnackbarComponent,
      {
        data,
        duration: 0,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: [
          'gd-status-snackbar',
          kind === 'offline' ? 'snack-warning' : 'snack-info',
        ],
        politeness: 'assertive',
      },
    );
    this.activeToastKind = kind;
    this.activeToastRef = snackBarRef;

    snackBarRef.afterDismissed().subscribe(() => {
      if (this.activeToastRef !== snackBarRef) return;
      this.activeToastRef = null;
      this.activeToastKind = null;
    });
  }

  private dismissActiveToast(): void {
    this.activeToastRef?.dismiss();
    this.activeToastRef = null;
    this.activeToastKind = null;
  }
}
